/**
 * Katie & Laura — Wish list page (owner + public share)
 *
 * Minted links shown to customers are shop-hosted:
 *   {origin}/pages/wish-list?share={token}
 * Worker workers.dev /share/{token} JSON URLs are rewritten here.
 * Public view fetches App Proxy GET /apps/kl-wishlist/share/{token},
 * then the CORS Worker URL. Never sends WISHLIST_PROXY_SECRET.
 * Notes are never shown on the public grid.
 */
(function (global) {
  'use strict';

  var SHARE_PAGE_PATH = '/pages/wish-list';
  var WORKER_SHARE_ORIGIN = 'https://katie-laura-wishlist.katie-and-laura.workers.dev';
  var HEART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path class="kl-wishlist-btn__heart" d="M12.1 20.15s-7.05-4.38-9.35-8.15C.7 8.7 2.05 4.55 5.7 3.4c2.05-.65 4.15.15 5.35 1.85 1.2-1.7 3.3-2.5 5.35-1.85 3.65 1.15 5 5.3 2.95 8.6-2.3 3.77-9.2 8.15-9.2 8.15z"/></svg>';

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function shopOrigin(origin) {
    var base = String(origin || '').replace(/\/+$/, '');
    if (!base && typeof location !== 'undefined' && location.origin) base = location.origin;
    return base;
  }

  function sharePagePath(root) {
    var fromAttr = root && root.getAttribute && String(root.getAttribute('data-share-page') || '').trim();
    if (fromAttr) {
      return fromAttr.charAt(0) === '/' ? fromAttr.replace(/\/+$/, '') : '/' + fromAttr.replace(/\/+$/, '');
    }
    if (typeof location !== 'undefined' && /\/pages\//.test(location.pathname || '')) {
      return String(location.pathname).replace(/\/+$/, '') || SHARE_PAGE_PATH;
    }
    return SHARE_PAGE_PATH;
  }

  function shopShareUrl(token, origin, root) {
    var t = String(token || '').trim();
    if (!t) return '';
    return shopOrigin(origin) + sharePagePath(root) + '?share=' + encodeURIComponent(t);
  }

  function tokenFromPayload(data) {
    if (!data || typeof data !== 'object') return '';
    if (data.token) return String(data.token).trim();
    var src = String(data.url || data.path || '');
    var query = src.match(/[?&#]share=([^&]+)/);
    if (query && query[1]) {
      try {
        return decodeURIComponent(query[1]).trim();
      } catch (e) {
        return query[1];
      }
    }
    var path = src.match(/\/share\/([^/?#]+)/);
    if (path && path[1]) {
      try {
        return decodeURIComponent(path[1]).trim();
      } catch (e2) {
        return path[1];
      }
    }
    return '';
  }

  function hostOf(href, origin) {
    try {
      return new URL(href, shopOrigin(origin) || 'https://placeholder.invalid').host;
    } catch (e) {
      return '';
    }
  }

  function isShopHostUrl(href, origin) {
    var shop = hostOf(shopOrigin(origin) + '/', origin);
    var host = hostOf(href, origin);
    return !!(shop && host && shop === host);
  }

  function isJsonShareEndpoint(href) {
    var s = String(href || '');
    if (!s) return false;
    if (/\/pages\/wish-list\?share=/i.test(s)) return false;
    if (/[?&]share=/i.test(s) && /\/pages\//i.test(s)) return false;
    return /\/share\/[^/?#]+/i.test(s);
  }

  function preferMintedUrl(url, origin) {
    var href = String(url || '').trim();
    if (!href) return false;
    if (isJsonShareEndpoint(href)) return false;
    if (/workers\.dev/i.test(href)) return false;
    if (/\/pages\/wish-list\?share=/i.test(href)) return true;
    return isShopHostUrl(href, origin);
  }

  function absoluteFrom(href, origin) {
    var value = String(href || '').trim();
    if (!value) return '';
    if (/^(https?:)?\/\//i.test(value)) {
      if (value.indexOf('//') === 0) return 'https:' + value;
      return value;
    }
    var base = shopOrigin(origin);
    return base + (value.charAt(0) === '/' ? value : '/' + value);
  }

  function resolveShareUrl(data, origin, root) {
    data = data || {};
    var token = tokenFromPayload(data);
    var url = data.url != null ? String(data.url).trim() : '';
    var path = data.path != null ? String(data.path).trim() : '';
    if (preferMintedUrl(url, origin)) return absoluteFrom(url, origin);
    if (preferMintedUrl(path, origin)) return absoluteFrom(path, origin);
    if (token) return shopShareUrl(token, origin, root);
    return '';
  }

  function shareTokenFromLocation(loc) {
    loc = loc || (typeof location !== 'undefined' ? location : null);
    if (!loc) return '';
    try {
      var search = String(loc.search || '');
      if (search) {
        var params = new URLSearchParams(search.charAt(0) === '?' ? search.slice(1) : search);
        var t = params.get('share');
        if (t) return String(t).trim();
      }
      var hash = String(loc.hash || '');
      if (hash.charAt(0) === '#') hash = hash.slice(1);
      if (hash) {
        var hashQuery = hash.charAt(0) === '?' ? hash.slice(1) : hash;
        var hp = new URLSearchParams(hashQuery);
        var ht = hp.get('share');
        if (ht) return String(ht).trim();
      }
    } catch (e) {
      /* ignore */
    }
    return '';
  }

  function publicItems(data) {
    var raw = [];
    if (Array.isArray(data)) raw = data;
    else if (data && Array.isArray(data.items)) raw = data.items;
    else if (data && Array.isArray(data.wishlist)) raw = data.wishlist;
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var item = raw[i];
      if (!item || typeof item !== 'object') continue;
      out.push({
        productId: item.productId ? String(item.productId) : '',
        variantId: item.variantId ? String(item.variantId) : '',
        handle: item.handle ? String(item.handle) : '',
        title: item.title ? String(item.title) : '',
        image: item.image ? String(item.image) : '',
        addedAt: item.addedAt ? String(item.addedAt) : ''
      });
    }
    return out;
  }

  function looksLikeShareJson(data) {
    return !!(data && typeof data === 'object' && (
      Array.isArray(data.items) || Array.isArray(data.wishlist) || Array.isArray(data)
    ));
  }

  function productUrl(rootUrl, handle) {
    if (!handle) return '';
    var base = rootUrl || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + 'products/' + encodeURIComponent(handle);
  }

  function sharedCardHtml(item, rootUrl, includeTitles) {
    var href = productUrl(rootUrl, item.handle);
    var img = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="" width="600" height="750" loading="lazy">'
      : '<span class="kl-wish-card__ph" aria-hidden="true"></span>';
    var media = href
      ? '<a class="kl-wish-card__media" href="' + escapeHtml(href) + '">' + img + '</a>'
      : '<div class="kl-wish-card__media">' + img + '</div>';
    var titleBlock = '';
    if (includeTitles) {
      var title = escapeHtml(item.title || item.handle || 'Saved piece');
      var titleEl = href
        ? '<a class="kl-wish-card__title" href="' + escapeHtml(href) + '">' + title + '</a>'
        : '<span class="kl-wish-card__title">' + title + '</span>';
      titleBlock = '<div class="kl-wish-card__body">' + titleEl + '</div>';
    }
    return '<li class="kl-wish-card kl-wish-card--shared">' + media + titleBlock + '</li>';
  }

  var pendingRemoveConfirm = null;

  function confirmRemove(cb) {
    if (typeof cb !== 'function') return;
    var page = typeof document !== 'undefined' ? document.querySelector('[data-kl-wishlist-page]') : null;
    var dialog = page && page.querySelector('[data-kl-wish-confirm]');
    var message = (page && page.getAttribute('data-remove-confirm')) || 'Remove from Wishlist?';
    if (!dialog || typeof dialog.showModal !== 'function') {
      cb(typeof global.confirm === 'function' ? !!global.confirm(message) : false);
      return;
    }
    if (dialog.open || pendingRemoveConfirm) return;
    pendingRemoveConfirm = cb;
    dialog.showModal();
  }

  function bindRemoveConfirm(root) {
    var dialog = root && root.querySelector('[data-kl-wish-confirm]');
    if (!dialog || dialog.getAttribute('data-kl-confirm-bound') === '1') return;
    dialog.setAttribute('data-kl-confirm-bound', '1');

    function settle(ok) {
      var fn = pendingRemoveConfirm;
      pendingRemoveConfirm = null;
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      if (fn) fn(!!ok);
    }

    var okBtn = dialog.querySelector('[data-kl-wish-confirm-ok]');
    if (okBtn) {
      okBtn.addEventListener('click', function (event) {
        event.preventDefault();
        settle(true);
      });
    }
    dialog.addEventListener('close', function () {
      var fn = pendingRemoveConfirm;
      pendingRemoveConfirm = null;
      if (fn) fn(false);
    });
  }

  var exported = {
    SHARE_PAGE_PATH: SHARE_PAGE_PATH,
    WORKER_SHARE_ORIGIN: WORKER_SHARE_ORIGIN,
    tokenFromPayload: tokenFromPayload,
    resolveShareUrl: resolveShareUrl,
    shareTokenFromLocation: shareTokenFromLocation,
    publicItems: publicItems,
    preferMintedUrl: preferMintedUrl,
    looksLikeShareJson: looksLikeShareJson,
    sharedCardHtml: sharedCardHtml,
    productUrl: productUrl,
    confirmRemove: confirmRemove
  };

  global.KLWishPage = exported;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = exported;
    return;
  }

  function api() {
    return global.KLWishlist || null;
  }

  function currentList() {
    var wish = api();
    return wish && wish.list ? wish.list() : [];
  }

  function proxyPathFrom(root) {
    var cfg = global.KLWishlistConfig || {};
    var raw = String((root && root.getAttribute('data-proxy')) || cfg.appProxyPath || '/apps/kl-wishlist').trim();
    if (!raw) raw = '/apps/kl-wishlist';
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function workerShareOrigin(root) {
    var cfg = global.KLWishlistConfig || {};
    var raw = String(
      (root && root.getAttribute('data-worker')) ||
        cfg.workerUrl ||
        WORKER_SHARE_ORIGIN
    ).trim();
    if (!raw) raw = WORKER_SHARE_ORIGIN;
    raw = raw.replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(raw)) return WORKER_SHARE_ORIGIN;
    return raw;
  }

  function ownerCardHtml(item, rootUrl, labels) {
    var href = productUrl(rootUrl, item.handle);
    var title = escapeHtml(item.title || item.handle || 'Saved piece');
    var img = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="" width="600" height="750" loading="lazy">'
      : '<span class="kl-wish-card__ph" aria-hidden="true"></span>';
    var media = href
      ? '<a class="kl-wish-card__media" href="' + escapeHtml(href) + '">' + img + '</a>'
      : '<div class="kl-wish-card__media">' + img + '</div>';
    var titleEl = href
      ? '<a class="kl-wish-card__title" href="' + escapeHtml(href) + '">' + title + '</a>'
      : '<span class="kl-wish-card__title">' + title + '</span>';
    var noteId = 'kl-wish-note-' + escapeHtml(item.productId);
    var note = item.note ? String(item.note) : '';

    return (
      '<li class="kl-wish-card" data-product-id="' + escapeHtml(item.productId) + '">' +
        media +
        '<div class="kl-wish-card__body">' +
          titleEl +
          '<button type="button" class="kl-wishlist-btn kl-wishlist-btn--page" data-kl-wishlist-toggle' +
            ' data-product-id="' + escapeHtml(item.productId) + '"' +
            ' data-variant-id="' + escapeHtml(item.variantId) + '"' +
            ' data-handle="' + escapeHtml(item.handle) + '"' +
            ' data-title="' + escapeHtml(item.title) + '"' +
            ' data-image="' + escapeHtml(item.image) + '"' +
            ' aria-pressed="true" aria-label="' + escapeHtml(labels.remove) + '">' +
            HEART +
          '</button>' +
        '</div>' +
        '<div class="kl-wish-card__note">' +
          '<label class="kl-wish-card__note-label" for="' + noteId + '">' + escapeHtml(labels.noteLabel) + '</label>' +
          '<p class="kl-wish-card__note-hint">' + escapeHtml(labels.noteHint) + '</p>' +
          '<textarea id="' + noteId + '" class="kl-wish-card__note-input" data-kl-wish-note' +
            ' data-product-id="' + escapeHtml(item.productId) + '"' +
            ' data-variant-id="' + escapeHtml(item.variantId) + '"' +
            ' rows="2" maxlength="500" placeholder="' + escapeHtml(labels.notePlaceholder) + '">' +
            escapeHtml(note) +
          '</textarea>' +
          '<div class="kl-wish-card__note-actions">' +
            '<button type="button" class="kl-wish-card__note-save" data-kl-wish-note-save>' +
              escapeHtml(labels.noteSave) +
            '</button>' +
            '<span class="kl-wish-card__note-status" data-kl-wish-note-status role="status" aria-live="polite" hidden>' +
              escapeHtml(labels.noteSaved) +
            '</span>' +
          '</div>' +
        '</div>' +
      '</li>'
    );
  }

  function requestShare(root) {
    var wish = api();
    if (wish && typeof wish.createShare === 'function') {
      return wish.createShare({ includeTitles: false, includeNotes: false });
    }
    return fetch(proxyPathFrom(root) + '/wishlist/share', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ includeTitles: false, includeNotes: false })
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-wishlist ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function fetchShareJson(url, opts) {
    return fetch(url, opts).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-wishlist ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().then(function (data) {
        if (!looksLikeShareJson(data)) {
          var invalid = new Error('kl-wishlist invalid');
          invalid.status = 404;
          throw invalid;
        }
        return data;
      });
    });
  }

  function fetchPublicShare(token, root) {
    var encoded = encodeURIComponent(token);
    var proxyUrl = proxyPathFrom(root) + '/share/' + encoded;
    var workerUrl = workerShareOrigin(root) + '/share/' + encoded;
    return fetchShareJson(proxyUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).catch(function () {
      return fetchShareJson(workerUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' }
      });
    });
  }

  function copyShareLink(input) {
    var text = input && input.value ? String(input.value) : '';
    if (!text) return Promise.reject();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return Promise.reject();
  }

  function fallbackSelectCopy(input, fallbackEl) {
    if (!input) return false;
    input.focus();
    input.select();
    if (typeof input.setSelectionRange === 'function') {
      input.setSelectionRange(0, input.value.length);
    }
    var copied = false;
    try {
      copied = !!(document.execCommand && document.execCommand('copy'));
    } catch (e) {
      copied = false;
    }
    if (!copied && fallbackEl) fallbackEl.hidden = false;
    return copied;
  }

  function shareStatusCode(err) {
    if (!err) return 0;
    if (err.status) return Number(err.status) || 0;
    if (err.code === 'sign-in') return 401;
    var match = String(err.message || '').match(/kl-wishlist\s+(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  function bindShare(root) {
    var wrap = root.querySelector('[data-kl-wish-share]');
    if (!wrap) return;
    var btn = wrap.querySelector('[data-kl-wish-share-btn]');
    var panel = wrap.querySelector('[data-kl-wish-share-panel]');
    var guestEl = wrap.querySelector('[data-kl-wish-share-guest]');
    var resultEl = wrap.querySelector('[data-kl-wish-share-result]');
    var urlInput = wrap.querySelector('[data-kl-wish-share-url]');
    var copyBtn = wrap.querySelector('[data-kl-wish-share-copy]');
    var fallbackEl = wrap.querySelector('[data-kl-wish-share-fallback]');
    var statusEl = wrap.querySelector('[data-kl-wish-share-status]');
    var toastEl = wrap.querySelector('[data-kl-wish-share-toast]');
    var loginEl = wrap.querySelector('[data-kl-wish-share-login]');
    if (!btn || !panel) return;

    var loggedIn = root.getAttribute('data-logged-in') === 'true';
    var origin = root.getAttribute('data-origin') || (typeof location !== 'undefined' ? location.origin : '');
    var copiedMsg = root.getAttribute('data-share-copied') || 'Link copied';
    var errorMsg = root.getAttribute('data-share-error') || 'We could not create a share link just now.';
    var creatingMsg = root.getAttribute('data-share-creating') || 'Making your link…';
    var loginUrl = root.getAttribute('data-login') || root.getAttribute('data-account') || '/account/login';
    if (loginEl && loginUrl) loginEl.setAttribute('href', loginUrl);

    var toastTimer = null;
    var busy = false;
    var lastLink = '';

    function showToast(message) {
      if (!toastEl) return;
      toastEl.textContent = message;
      toastEl.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toastEl.hidden = true;
      }, 2400);
    }

    function setStatus(message, isError) {
      if (!statusEl) return;
      if (!message) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.classList.remove('kl-wish-share__status--error');
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.classList.toggle('kl-wish-share__status--error', !!isError);
    }

    function showGuest() {
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      if (guestEl) guestEl.hidden = false;
      if (resultEl) resultEl.hidden = true;
      setStatus('');
    }

    function showResult(link) {
      lastLink = link;
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      if (guestEl) guestEl.hidden = true;
      if (resultEl) resultEl.hidden = false;
      if (fallbackEl) fallbackEl.hidden = true;
      if (urlInput) urlInput.value = link;
      setStatus('');
    }

    function afterCopy(ok) {
      if (ok) {
        showToast(copiedMsg);
        if (fallbackEl) fallbackEl.hidden = true;
        return;
      }
      if (fallbackSelectCopy(urlInput, fallbackEl)) {
        showToast(copiedMsg);
      }
    }

    function createAndShow() {
      if (busy) return;
      if (lastLink) {
        showResult(lastLink);
        copyShareLink(urlInput).then(function () {
          afterCopy(true);
        }).catch(function () {
          afterCopy(false);
        });
        return;
      }
      busy = true;
      btn.disabled = true;
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      if (guestEl) guestEl.hidden = true;
      if (resultEl) resultEl.hidden = true;
      setStatus(creatingMsg, false);
      requestShare(root)
        .then(function (data) {
          var link = resolveShareUrl(data || {}, origin, root);
          if (!link) throw new Error('missing-url');
          showResult(link);
          return copyShareLink(urlInput).then(function () {
            afterCopy(true);
          }).catch(function () {
            afterCopy(false);
          });
        })
        .catch(function (err) {
          var status = shareStatusCode(err);
          if (status === 401 || status === 403 || (err && err.code === 'sign-in')) {
            showGuest();
          } else {
            setStatus(errorMsg, true);
          }
        })
        .then(function () {
          busy = false;
          btn.disabled = false;
        });
    }

    btn.addEventListener('click', function () {
      var cfg = global.KLWishlistConfig || {};
      if (!loggedIn && !cfg.loggedIn) {
        showGuest();
        return;
      }
      createAndShow();
    });

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        if (!urlInput || !urlInput.value) return;
        copyShareLink(urlInput).then(function () {
          afterCopy(true);
        }).catch(function () {
          afterCopy(false);
        });
      });
    }
  }

  function bindOwner(root) {
    bindShare(root);
    bindRemoveConfirm(root);

    var grid = root.querySelector('[data-kl-wish-grid]');
    var emptyEl = root.querySelector('[data-kl-wish-empty]');
    if (!grid || !emptyEl) return;

    var loggedIn = root.getAttribute('data-logged-in') === 'true';
    var rootUrl = root.getAttribute('data-root') || '/';
    var labels = {
      remove: root.getAttribute('data-remove') || 'Remove from wishlist',
      noteLabel: root.getAttribute('data-note-label') || 'Your note',
      noteHint: root.getAttribute('data-note-hint') || 'Just for you — not on shared lists.',
      notePlaceholder: root.getAttribute('data-note-placeholder') || 'A thought, just for you…',
      noteSave: root.getAttribute('data-note-save') || 'Save',
      noteSaved: root.getAttribute('data-note-saved') || 'Saved'
    };
    var paintedKey = '';
    var savedNotes = {};

    function collectDrafts() {
      var drafts = {};
      var tas = grid.querySelectorAll('[data-kl-wish-note]');
      for (var i = 0; i < tas.length; i++) {
        var id = tas[i].getAttribute('data-product-id');
        if (id) drafts[id] = tas[i].value;
      }
      return drafts;
    }

    function showNoteSaved(card) {
      var status = card && card.querySelector('[data-kl-wish-note-status]');
      if (!status) return;
      status.hidden = false;
      clearTimeout(card._klNoteTimer);
      card._klNoteTimer = setTimeout(function () {
        status.hidden = true;
      }, 2000);
    }

    function saveNoteFrom(el) {
      var card = el && el.closest && el.closest('.kl-wish-card');
      var ta = card && card.querySelector('[data-kl-wish-note]');
      var wish = api();
      if (!ta || !wish || !wish.setNote) return;
      var productId = ta.getAttribute('data-product-id') || '';
      var next = ta.value;
      var prev = savedNotes[productId] != null ? savedNotes[productId] : '';
      if (next === prev) {
        if (el !== ta) showNoteSaved(card);
        return;
      }
      var id = ta.getAttribute('data-variant-id') || productId;
      wish.setNote(id, next);
      savedNotes[productId] = next;
      showNoteSaved(card);
    }

    function paint(items) {
      var drafts = collectDrafts();
      var active = document.activeElement;
      var focusedId = '';
      if (active && grid.contains(active) && active.getAttribute && active.getAttribute('data-kl-wish-note') != null) {
        focusedId = active.getAttribute('data-product-id') || '';
      }

      var list = (items || []).slice().sort(function (a, b) {
        return String(b.addedAt || '').localeCompare(String(a.addedAt || ''));
      }).map(function (item) {
        var draft = drafts[item.productId];
        if (draft == null) return item;
        return Object.assign({}, item, { note: draft });
      });

      var key = list
        .map(function (item) {
          return item.productId;
        })
        .join(',');

      if (!list.length) {
        paintedKey = '';
        grid.innerHTML = '';
        grid.hidden = true;
        emptyEl.hidden = false;
        return;
      }

      if (key === paintedKey && focusedId && grid.children.length === list.length) {
        return;
      }

      emptyEl.hidden = true;
      grid.hidden = false;
      paintedKey = key;
      list.forEach(function (item) {
        if (savedNotes[item.productId] == null) savedNotes[item.productId] = item.note || '';
      });
      grid.innerHTML = list
        .map(function (item) {
          return ownerCardHtml(item, rootUrl, labels);
        })
        .join('');

      if (focusedId) {
        var ta = grid.querySelector('[data-kl-wish-note][data-product-id="' + focusedId + '"]');
        if (ta) {
          ta.focus();
          if (typeof ta.setSelectionRange === 'function') {
            var end = ta.value.length;
            ta.setSelectionRange(end, end);
          }
        }
      }
    }

    function refresh() {
      paint(currentList());
    }

    grid.addEventListener('focusout', function (event) {
      if (event.target && event.target.getAttribute && event.target.getAttribute('data-kl-wish-note') != null) {
        saveNoteFrom(event.target);
      }
    });

    grid.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest && event.target.closest('[data-kl-wish-note-save]');
      if (!btn) return;
      event.preventDefault();
      saveNoteFrom(btn);
    });

    document.addEventListener('wishlist:add', refresh);
    document.addEventListener('wishlist:remove', refresh);
    document.addEventListener('wishlist:toggle', refresh);
    document.addEventListener('wishlist:sync', refresh);

    var items = currentList();
    paint(items);

    var wish = api();
    if (loggedIn && wish && wish.refreshFromProxy) {
      wish.refreshFromProxy().then(function (next) {
        paint(next || currentList());
      });
    }
  }

  function bindPublic(root, token) {
    root.classList.add('kl-wish-page--shared');
    root.setAttribute('data-kl-wish-mode', 'shared');

    var shareWrap = root.querySelector('[data-kl-wish-share]');
    if (shareWrap) shareWrap.hidden = true;

    var kicker = root.querySelector('[data-kl-wish-shared-kicker]');
    if (kicker) kicker.hidden = false;

    var grid = root.querySelector('[data-kl-wish-grid]');
    var emptyEl = root.querySelector('[data-kl-wish-empty]');
    if (!grid || !emptyEl) return;

    var rootUrl = root.getAttribute('data-root') || '/';
    var loadingMsg = root.getAttribute('data-shared-loading') || 'Opening a shared list…';
    var emptyMsg = root.getAttribute('data-shared-empty') || 'This shared list is empty.';
    var errorMsg = root.getAttribute('data-shared-error') || 'This shared list isn’t available just now.';
    var shopLink = emptyEl.querySelector('.kl-wish-page__shop');
    if (shopLink) shopLink.hidden = true;

    emptyEl.hidden = false;
    emptyEl.setAttribute('data-kl-wish-empty-copy', emptyEl.textContent || '');
    var loadingNode = emptyEl.querySelector('[data-kl-wish-empty-text]');
    if (loadingNode) loadingNode.textContent = loadingMsg;
    else emptyEl.textContent = loadingMsg;
    grid.hidden = true;
    grid.innerHTML = '';

    fetchPublicShare(token, root)
      .then(function (data) {
        var includeTitles = !!(data && data.includeTitles);
        var items = publicItems(data);
        if (!items.length) {
          if (loadingNode) loadingNode.textContent = emptyMsg;
          else emptyEl.textContent = emptyMsg;
          emptyEl.hidden = false;
          grid.hidden = true;
          return;
        }
        emptyEl.hidden = true;
        grid.hidden = false;
        grid.innerHTML = items
          .map(function (item) {
            return sharedCardHtml(item, rootUrl, includeTitles);
          })
          .join('');
      })
      .catch(function () {
        if (loadingNode) loadingNode.textContent = errorMsg;
        else emptyEl.textContent = errorMsg;
        emptyEl.hidden = false;
        grid.hidden = true;
      });
  }

  function bindRoot(root) {
    if (!root || root.getAttribute('data-kl-bound') === '1') return;
    root.setAttribute('data-kl-bound', '1');
    var token = shareTokenFromLocation();
    if (token) {
      bindPublic(root, token);
      return;
    }
    bindOwner(root);
  }

  function waitForApi(cb) {
    if (api() && api().list) return cb();
    var n = 0;
    var timer = setInterval(function () {
      if (api() && api().list) {
        clearInterval(timer);
        cb();
      } else if (++n > 50) {
        clearInterval(timer);
        cb();
      }
    }, 50);
  }

  function ready(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  }

  function start() {
    var root = document.querySelector('[data-kl-wishlist-page]');
    if (!root) return;
    if (shareTokenFromLocation()) {
      bindRoot(root);
      return;
    }
    waitForApi(function () {
      bindRoot(root);
    });
  }

  ready(start);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-kl-wishlist-page]');
    if (root) {
      root.removeAttribute('data-kl-bound');
      bindRoot(root);
    }
  });
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
